// Translation status management utilities
import { TranslationStatus, TranslationMetadata, LessonSection } from '../types/lessonPlan.types';

/**
 * Generate a simple hash of content for comparison
 * Uses a basic string hash algorithm for detecting content changes
 */
export function generateContentHash(content: string): string {
  if (!content) return '';
  
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
}

/**
 * Check if a translation is outdated by comparing content hashes
 */
export function isTranslationOutdated(
  currentContent: string,
  translationMeta?: TranslationMetadata
): boolean {
  if (!translationMeta) return true;
  if (!currentContent) return false;
  
  const currentHash = generateContentHash(currentContent);
  return currentHash !== translationMeta.sourceContentHash;
}

/**
 * Determine the translation status for a section
 */
export function getTranslationStatus(
  section: LessonSection,
  translationType: 'story' | 'activities'
): TranslationStatus {
  const hasTranslation = translationType === 'story' 
    ? !!section.storySpanish?.content 
    : !!section.activitiesSpanish;
  
  if (!hasTranslation) return 'missing';
  
  const metadata = translationType === 'story'
    ? section.translationMetadata?.storySpanish
    : section.translationMetadata?.activitiesSpanish;
  
  if (!metadata) return 'valid'; // Assume valid if no metadata (backward compatibility)
  
  if (metadata.status === 'error') return 'error';
  
  const sourceContent = translationType === 'story'
    ? section.story?.content || ''
    : section.activities;
  
  if (isTranslationOutdated(sourceContent, metadata)) {
    return 'outdated';
  }
  
  return 'valid';
}

/**
 * Create translation metadata for newly generated translations
 */
export function createTranslationMetadata(
  sourceContent: string,
  language: string
): TranslationMetadata {
  return {
    lastUpdated: new Date(),
    sourceContentHash: generateContentHash(sourceContent),
    status: 'valid',
    language
  };
}

/**
 * Get user-friendly status message
 */
export function getTranslationStatusMessage(status: TranslationStatus): string {
  switch (status) {
    case 'valid':
      return 'Translation is up to date';
    case 'outdated':
      return 'Translation needs updating (content changed)';
    case 'missing':
      return 'No translation available';
    case 'error':
      return 'Translation error occurred';
    default:
      return 'Unknown status';
  }
}

/**
 * Get status color for UI display
 */
export function getTranslationStatusColor(status: TranslationStatus): string {
  switch (status) {
    case 'valid':
      return '#4caf50'; // green
    case 'outdated':
      return '#ff9800'; // orange
    case 'missing':
      return '#9e9e9e'; // gray
    case 'error':
      return '#f44336'; // red
    default:
      return '#9e9e9e';
  }
}

/**
 * Get status icon for UI display
 */
export function getTranslationStatusIcon(status: TranslationStatus): string {
  switch (status) {
    case 'valid':
      return '✓'; // checkmark
    case 'outdated':
      return '⚠'; // warning
    case 'missing':
      return '○'; // empty circle
    case 'error':
      return '✕'; // x mark
    default:
      return '?';
  }
}

